// Canonical class ↔ instructor staffing layer.
//
// SINGLE SOURCE OF TRUTH: public.instructor_class_assignments.
// Both directions of the relationship (Class Card → Instructor, Instructor
// Card → Classes) are derived from the SAME rows here. `classes.instructor_id`
// is administrative metadata only (class contact / historical default) and is
// never written by this module, never counted as staffing.
//
// Session-aware: every assignment carries a session_label plus an effective
// date window. Changing the instructor for an upcoming session NEVER rewrites
// an assignment whose window already closed — history is preserved by ending
// the outgoing row, not by editing or deleting it.
import { supabase } from "@/integrations/supabase/client";
import {
  resolveOperationalInstructors,
  type OperationalAssignmentRow,
} from "@/lib/operationalInstructors";
import { resolveNextSessionOption } from "@/lib/nextClassSession";
import type { ClassSession } from "@/data/classes";

// Canonical lifecycle vocabulary — must stay inside the DB `ica_status_check`
// constraint: proposed | active | upcoming | future | temporary | completed |
// cancelled | on_hold | historical | replaced.
export const LIVE_ASSIGNMENT_STATUSES = ["active", "upcoming", "future", "temporary"] as const;
/** Terminal statuses used when an assignment stops without being deleted. */
export const ASSIGNMENT_END_STATUS = {
  /** Instructor taught (or could have taught) up to the end date, then stopped. */
  ended: "completed",
  /** Handed off to a different instructor from the effective date. */
  replaced: "replaced",
  /** Never started — the window had not opened yet. */
  cancelled: "cancelled",
} as const;
export type AssignmentEndReason = keyof typeof ASSIGNMENT_END_STATUS;

export interface AssignmentRow extends OperationalAssignmentRow {
  id: string;
  instructor_id: string;
  class_slug: string;
  class_id: string | null;
  class_title: string | null;
  session_label: string | null;
  status: string;
  start_date: string | null;
  end_date: string | null;
  day_of_week: string | null;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  assignment_kind: string;
  notes: string | null;
  updated_at: string;
  /** Assignment-level compensation — the rate payroll actually resolves. */
  compensation_mode: string | null;
  compensation_rate_cents: number | null;
  compensation_effective_date: string | null;
}

export interface InstructorRow {
  id: string;
  full_name: string;
  preferred_name: string | null;
  status: string;
  email: string | null;
}

export interface ClassRow {
  id: string;
  slug: string;
  title: string;
  status: string;
  schedule: string | null;
  preferred_days: string | null;
  preferred_time: string | null;
  location: string | null;
  room: string | null;
  capacity: number | null;
  instructor_id: string | null;
  /** The class's OWN configured sessions — the only valid session anchor. */
  sessions?: unknown;
  management_type?: string | null;
  pricing_model?: string | null;
  schedule_model?: string | null;
  min_enrollment?: number | null;
}


export interface PeriodRow {
  id: string;
  name: string;
  display_label: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
  is_visible: boolean;
  class_ids: string[] | null;
  sort_order: number | null;
}

export interface SessionWindow {
  label: string;
  start: string | null;
  end: string | null;
}

export const today = () => new Date().toISOString().slice(0, 10);

export function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * CANONICAL session anchor for a class — identical to the one /admin/classes
 * and the Class Editor use.
 *
 * 1. The class's OWN configured sessions (`classes.sessions`) win. This is the
 *    same `resolveNextSessionOption` the class card uses, so the two pages can
 *    never scope to different offerings.
 * 2. Only if the class has no sessions of its own may a registration_period
 *    anchor it, and then ONLY a period explicitly scoped to this class.
 *    Hub-wide calendar buckets ("August", "Fall Semester" with no class_ids)
 *    are NOT class sessions — treating them as such used to scope an ongoing
 *    club to "August" and hide its standing September assignment.
 * 3. Otherwise null — a variable / ongoing class that must not be scoped.
 */
export function currentSessionFor(cls: ClassRow, periods: PeriodRow[]): SessionWindow | null {
  const own = resolveNextSessionOption({
    sessions: (cls.sessions as ClassSession[] | null) ?? null,
    scheduleText: cls.schedule,
    managementType: (cls.management_type as "silver_sage" | "external_provider" | null) ?? undefined,
    visibilityStatus: cls.status,
  });
  if (own) {
    return {
      label: own.label,
      start: own.start ? own.start.toISOString().slice(0, 10) : null,
      end: own.end ? own.end.toISOString().slice(0, 10) : null,
    };
  }

  const t = today();
  const p = periods
    .filter((p) => p.is_active && p.is_visible)
    .filter((p) => p.class_ids?.includes(cls.id))
    .filter((p) => !p.end_date || p.end_date >= t)
    .sort((a, b) => (a.start_date ?? "").localeCompare(b.start_date ?? ""))[0];
  if (!p) return null;
  return { label: p.display_label || p.name, start: p.start_date, end: p.end_date };
}

export function assignmentsForClass(rows: AssignmentRow[], cls: ClassRow): AssignmentRow[] {
  return rows.filter((r) => r.class_id === cls.id || r.class_slug === cls.slug);
}



export function isLive(r: AssignmentRow): boolean {
  if (!(LIVE_ASSIGNMENT_STATUSES as readonly string[]).includes((r.status ?? "").toLowerCase())) {
    return false;
  }
  return !r.end_date || r.end_date >= today();
}

/** The operational assignment rows staffing a class's current/upcoming session. */
export function staffingForClass(
  rows: AssignmentRow[],
  cls: ClassRow,
  session: SessionWindow | null,
): { rows: AssignmentRow[]; instructorIds: string[]; pending: boolean } {
  const scoped = assignmentsForClass(rows, cls);
  const result = resolveOperationalInstructors(scoped, {
    sessionLabel: session?.label ?? null,
    sessionStart: session?.start ?? null,
    sessionEnd: session?.end ?? null,
  });
  const ids = result.instructorIds;
  return {
    rows: scoped.filter((r) => result.rows.some((x) => (x as AssignmentRow).id === r.id)),
    instructorIds: ids,
    pending: result.pending,
  };
}

export function instructorName(i: InstructorRow | undefined | null): string {
  if (!i) return "Unknown instructor";
  return i.preferred_name?.trim() || i.full_name;
}

// -----------------------------------------------------------------------------
// Loading
// -----------------------------------------------------------------------------

export interface StaffingData {
  classes: ClassRow[];
  instructors: InstructorRow[];
  assignments: AssignmentRow[];
  periods: PeriodRow[];
}

export async function fetchStaffingData(): Promise<StaffingData> {
  const [cRes, iRes, aRes, pRes] = await Promise.all([
    supabase
      .from("classes")
      .select(
        "id,slug,title,status,schedule,preferred_days,preferred_time,location,room,capacity,instructor_id,sessions,management_type,pricing_model,schedule_model,min_enrollment",
      )
      .neq("status", "archived")
      .order("title"),
    supabase
      .from("instructors")
      .select("id,full_name,preferred_name,status,email")
      .neq("status", "merged")
      .order("full_name"),
    supabase
      .from("instructor_class_assignments")
      .select(
        "id,instructor_id,class_slug,class_id,class_title,session_label,status,start_date,end_date,day_of_week,start_time,end_time,location,assignment_kind,temporary_until_replaced,notes,updated_at,compensation_mode,compensation_rate_cents,compensation_effective_date",
      )
      .order("start_date", { ascending: false }),
    supabase
      .from("registration_periods")
      .select("id,name,display_label,start_date,end_date,is_active,is_visible,class_ids,sort_order")
      .order("start_date"),
  ]);
  return {
    classes: (cRes.data as ClassRow[]) ?? [],
    instructors: (iRes.data as InstructorRow[]) ?? [],
    assignments: (aRes.data as AssignmentRow[]) ?? [],
    periods: (pRes.data as PeriodRow[]) ?? [],
  };
}

// -----------------------------------------------------------------------------
// Payroll safety
// -----------------------------------------------------------------------------

export interface PayrollConflict {
  ledgerId: string;
  instructorId: string;
  paidAt: string | null;
  finalCents: number | null;
  dates: string[];
}

/**
 * Detects already-paid payroll that overlaps the window we're about to hand to
 * a different instructor. We NEVER rewrite these rows — we surface them and let
 * the admin decide. Only meetings on/after `effectiveDate` matter; anything
 * earlier is settled history and is untouched by an effective-dated change.
 */
export async function findPayrollConflicts(
  instructorId: string,
  classTitle: string,
  classSlug: string,
  effectiveDate: string,
): Promise<PayrollConflict[]> {
  const { data } = await supabase
    .from("payroll_ledger")
    .select("id,instructor_id,paid_at,final_cents,paid_occurrence_dates,classes_snapshot,voided_at")
    .eq("instructor_id", instructorId)
    .is("voided_at", null);
  const needle = [classTitle, classSlug].filter(Boolean).map((s) => s.toLowerCase());
  const out: PayrollConflict[] = [];
  for (const row of (data ?? []) as any[]) {
    const snap = JSON.stringify(row.classes_snapshot ?? "").toLowerCase();
    if (!needle.some((n) => snap.includes(n))) continue;
    const dates = ((row.paid_occurrence_dates ?? []) as string[]).filter(
      (d) => typeof d === "string" && d.slice(0, 10) >= effectiveDate,
    );
    if (!dates.length) continue;
    out.push({
      ledgerId: row.id,
      instructorId: row.instructor_id,
      paidAt: row.paid_at,
      finalCents: row.final_cents,
      dates,
    });
  }
  return out;
}

// -----------------------------------------------------------------------------
// Payroll-safe replacement dating
// -----------------------------------------------------------------------------

export interface EligibilityWindow {
  /** Latest meeting date that is already taught and/or already paid (ISO) or null. */
  settledThrough: string | null;
  /** Earliest date a replacement instructor may legally begin (ISO). */
  earliest: string;
  /** Instructor id whose history sets the floor, when known. */
  instructorId: string | null;
}

/**
 * Determine the earliest date a NEW instructor may start on a class, so that
 * already-taught / already-paid meetings can never be rewritten.
 *
 * Floor = the day after the latest of:
 *   • any paid payroll occurrence date for this class (any instructor), and
 *   • the last completed meeting date implied by the live assignment window.
 * Never earlier than today. When the class schedule names weekdays, the floor
 * is snapped forward to the next actual meeting weekday.
 */
export async function resolveEligibleStartDate(
  cls: ClassRow,
  outgoing: AssignmentRow[],
): Promise<EligibilityWindow> {
  const { data } = await supabase
    .from("payroll_ledger")
    .select("instructor_id,paid_at,paid_occurrence_dates,classes_snapshot,voided_at")
    .is("voided_at", null);
  const needle = [cls.title, cls.slug].filter(Boolean).map((s) => s.toLowerCase());
  let settled: string | null = null;
  let owner: string | null = null;
  for (const row of (data ?? []) as any[]) {
    if (!row.paid_at) continue; // only PAID history sets the floor
    const snap = JSON.stringify(row.classes_snapshot ?? "").toLowerCase();
    if (!needle.some((n) => snap.includes(n))) continue;
    for (const d of (row.paid_occurrence_dates ?? []) as string[]) {
      const iso = typeof d === "string" ? d.slice(0, 10) : "";
      if (iso && (!settled || iso > settled)) {
        settled = iso;
        owner = row.instructor_id;
      }
    }
  }
  // A live assignment that already started implies taught meetings up to today.
  for (const row of outgoing) {
    if (row.start_date && row.start_date <= today()) {
      if (!owner) owner = row.instructor_id;
    }
  }

  let earliest = settled ? addDays(settled, 1) : today();
  if (earliest < today()) earliest = today();
  earliest = snapToMeetingDay(earliest, cls);
  return { settledThrough: settled, earliest, instructorId: owner };
}

const DAY_ORDER = [
  "sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday",
];

/** Move `iso` forward to the next weekday the class actually meets, if known. */
export function snapToMeetingDay(iso: string, cls: ClassRow): string {
  const text = `${cls.schedule ?? ""} ${cls.preferred_days ?? ""}`.toLowerCase();
  const days = DAY_ORDER.map((d, i) => (text.includes(d) ? i : -1)).filter((i) => i >= 0);
  if (!days.length) return iso;
  for (let step = 0; step < 7; step += 1) {
    const candidate = addDays(iso, step);
    const dow = new Date(`${candidate}T00:00:00Z`).getUTCDay();
    if (days.includes(dow)) return candidate;
  }
  return iso;
}

// -----------------------------------------------------------------------------
// Mutations — all effective-dated, all history-preserving
// -----------------------------------------------------------------------------


export interface AssignInput {
  cls: ClassRow;
  session: SessionWindow | null;
  instructorId: string;
  effectiveDate: string;
  /** Live assignments being superseded (ended, never deleted). */
  supersede: AssignmentRow[];
  note?: string | null;
}

/**
 * End a live assignment as of `effectiveDate` without destroying history.
 * The row is NEVER deleted: it keeps its instructor, dates and payroll linkage
 * and simply moves to a terminal status the DB constraint accepts.
 */
export async function endAssignment(
  row: AssignmentRow,
  effectiveDate: string,
  reason: AssignmentEndReason,
) {
  // An assignment whose window never opened is cancelled outright; one already
  // running is closed the day before the effective date so taught meetings —
  // and the payroll derived from them — stay inside the assignment window.
  const startsLater = !!row.start_date && row.start_date >= effectiveDate;
  const patch = startsLater
    ? { status: ASSIGNMENT_END_STATUS.cancelled, end_date: row.end_date }
    : { status: ASSIGNMENT_END_STATUS[reason], end_date: addDays(effectiveDate, -1) };
  const { error } = await supabase
    .from("instructor_class_assignments")
    .update(patch as any)
    .eq("id", row.id);
  if (error) throw error;
}

export async function assignInstructor(input: AssignInput): Promise<void> {
  const { cls, session, instructorId, effectiveDate, supersede } = input;

  // Hard payroll-safety guard: a replacement may never begin on or before a
  // meeting that has already been paid out. Historical truth wins over intent.
  const window = await resolveEligibleStartDate(cls, supersede);
  if (window.settledThrough && effectiveDate <= window.settledThrough) {
    throw new Error(
      `This class has teaching/payroll history through ${window.settledThrough}. The earliest replacement date is ${window.earliest}.`,
    );
  }

  for (const row of supersede) {
    if (row.instructor_id === instructorId) continue; // already the right person
    await endAssignment(row, effectiveDate, "replaced");
  }


  const { error } = await supabase.from("instructor_class_assignments").insert({
    instructor_id: instructorId,
    class_id: cls.id,
    class_slug: cls.slug,
    class_title: cls.title,
    session_label: session?.label ?? null,
    status: effectiveDate > today() ? "upcoming" : "active",
    start_date: effectiveDate,
    end_date: session?.end ?? null,
    location: cls.location,
    assignment_kind: "permanent",
    notes: input.note ?? null,
  } as any);
  if (error) throw error;
}

export async function removeAssignment(row: AssignmentRow, effectiveDate: string): Promise<void> {
  await endAssignment(row, effectiveDate, "ended");
}

